import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Buffer } from 'buffer';
import { useWeightSimulation } from './useWeightSimulation';
import useLoadRiteScale from './useLoadRiteScale';

export const useScaleData = (configuredScaleType = null, config = null) => {
    const [currentWeight, setCurrentWeight] = useState(null);
    const [weightStatus, setWeightStatus] = useState('stable');
    const [connecting, setConnecting] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);
    
    // Dual scale support
    const [emptyScaleWeight, setEmptyScaleWeight] = useState(null);
    const [emptyScaleStatus, setEmptyScaleStatus] = useState('stable');
    const [fullScaleWeight, setFullScaleWeight] = useState(null);
    const [fullScaleStatus, setFullScaleStatus] = useState('stable');
    
    // Use ref to track if we're connected to prevent multiple connections
    const isConnectedRef = useRef(false);

    // Check if we're in dual scale mode
    const isDualScaleMode = config?.dualScaleMode || false;
    
    // Create separate configs for empty and full scales
    const emptyScaleConfig = isDualScaleMode ? {
        ...config,
        scaleType: config.emptyScaleType,
        scaleDevice: config.emptyScaleDevice,
        serialPort: config.emptySerialPort,
        loadriteAccessToken: config.emptyLoadriteAccessToken,
        loadriteSiteId: config.emptyLoadriteSiteId
    } : config;
    
    const fullScaleConfig = isDualScaleMode ? {
        ...config,
        scaleType: config.fullScaleType,
        scaleDevice: config.fullScaleDevice,
        serialPort: config.fullSerialPort,
        loadriteAccessToken: config.fullLoadriteAccessToken,
        loadriteSiteId: config.fullLoadriteSiteId
    } : config;

    const simulatedWeight = useWeightSimulation(configuredScaleType === 'simulation');
    const loadRiteScale = useLoadRiteScale(config);
    
    // Dual scale hooks
    const emptySimulatedWeight = useWeightSimulation(isDualScaleMode && config?.emptyScaleType === 'simulation');
    const fullSimulatedWeight = useWeightSimulation(isDualScaleMode && config?.fullScaleType === 'simulation');
    const emptyLoadRiteScale = useLoadRiteScale(isDualScaleMode && config?.emptyScaleType === 'loadrite' ? emptyScaleConfig : null);
    const fullLoadRiteScale = useLoadRiteScale(isDualScaleMode && config?.fullScaleType === 'loadrite' ? fullScaleConfig : null);
    
    // Debug: Log dual scale configuration
    useEffect(() => {
        if (isDualScaleMode) {
            // console.log('🔧 Dual scale mode configuration:');
            // console.log('  Empty scale type:', config?.emptyScaleType);
            // console.log('  Full scale type:', config?.fullScaleType);
        }
    }, [isDualScaleMode, config?.emptyScaleType, config?.fullScaleType]);

    // Handle simulation weight updates
    useEffect(() => {
        if (configuredScaleType === 'simulation' && simulatedWeight > 0) {
            // Convert kg to tons with proper formatting
            const weightInTons = (simulatedWeight / 1000).toFixed(2);
            setCurrentWeight(`${weightInTons} Tons`);
            setWeightStatus('stable');
        }
    }, [simulatedWeight, configuredScaleType]);

    // Handle LoadRite weight updates
    useEffect(() => {
        if (configuredScaleType === 'loadrite' && loadRiteScale?.currentWeight !== null && loadRiteScale?.currentWeight !== undefined) {
            setCurrentWeight(loadRiteScale.currentWeight);
            setWeightStatus(loadRiteScale.weightStatus);
        }
    }, [loadRiteScale?.currentWeight, loadRiteScale?.weightStatus, configuredScaleType]);

    // Handle dual scale empty scale weight updates
    useEffect(() => {
        if (isDualScaleMode && config?.emptyScaleType === 'simulation' && emptySimulatedWeight > 0) {
            const weightInTons = (emptySimulatedWeight / 1000).toFixed(2);
            setEmptyScaleWeight(`${weightInTons} Tons`);
            setEmptyScaleStatus('stable');
        } else if (isDualScaleMode && config?.emptyScaleType === 'loadrite' && emptyLoadRiteScale?.currentWeight !== null && emptyLoadRiteScale?.currentWeight !== undefined) {
            setEmptyScaleWeight(emptyLoadRiteScale.currentWeight);
            setEmptyScaleStatus(emptyLoadRiteScale.weightStatus);
        }
    }, [isDualScaleMode, config?.emptyScaleType, emptySimulatedWeight, emptyLoadRiteScale?.currentWeight, emptyLoadRiteScale?.weightStatus]);

    // Handle dual scale full scale weight updates
    useEffect(() => {
        if (isDualScaleMode && config?.fullScaleType === 'simulation' && fullSimulatedWeight > 0) {
            const weightInTons = (fullSimulatedWeight / 1000).toFixed(2);
            setFullScaleWeight(`${weightInTons} Tons`);
            setFullScaleStatus('stable');
        } else if (isDualScaleMode && config?.fullScaleType === 'loadrite' && fullLoadRiteScale?.currentWeight !== null && fullLoadRiteScale?.currentWeight !== undefined) {
            setFullScaleWeight(fullLoadRiteScale.currentWeight);
            setFullScaleStatus(fullLoadRiteScale.weightStatus);
        }
    }, [isDualScaleMode, config?.fullScaleType, fullSimulatedWeight, fullLoadRiteScale?.currentWeight, fullLoadRiteScale?.weightStatus]);

    useEffect(() => {
        if (configuredScaleType) {
            // console.log('Scale type changed to:', configuredScaleType);
            setIsTransitioning(true);
            
            // Clean up any existing listeners
            cleanupScaleDataListener();
            
            // Wait a moment before setting up new listeners
            setTimeout(() => {
                setIsTransitioning(false);
                setupScaleDataListener();
            }, 1000);
        }
    }, [configuredScaleType]);

    const handleScaleData = useCallback((data) => {
            try {
                // Ignore data during scale type transitions
            if (isTransitioning) {
                // console.log('Ignoring data during scale type transition');
                return;
            }
            
            // In dual scale mode, we need to determine which scale the data is from
            // For now, we'll assume it's from the empty scale if it's USB/Serial
            // TODO: In the future, we could add logic to determine which scale the data is from
            if (isDualScaleMode) {
                // Check if we have USB/Serial scales configured
                const hasEmptyUSB = config?.emptyScaleType === 'usb';
                const hasEmptySerial = config?.emptyScaleType === 'serial';
                const hasFullUSB = config?.fullScaleType === 'usb';
                const hasFullSerial = config?.fullScaleType === 'serial';
                
                if (hasEmptyUSB || hasEmptySerial || hasFullUSB || hasFullSerial) {
                    // console.log('📊 Dual scale mode: Processing USB/Serial data');
                    // For now, route all USB/Serial data to empty scale
                    // This is a limitation - we can't distinguish between scales yet
                    if (hasEmptyUSB || hasEmptySerial) {
                        // Process as empty scale data
                        if (config?.emptyScaleType === 'usb') {
                            handleUSBScaleData(data);
                        } else if (config?.emptyScaleType === 'serial') {
                            handleSerialScaleData(data);
                        }
                    } else if (hasFullUSB || hasFullSerial) {
                        // Process as full scale data (if empty scale is not USB/Serial)
                        if (config?.fullScaleType === 'usb') {
                            handleUSBScaleData(data);
                        } else if (config?.fullScaleType === 'serial') {
                            handleSerialScaleData(data);
                        }
                    }
                    return;
                }
            }
            
            // Only process data if we have a configured scale type
            if (!configuredScaleType) {
                console.warn('No scale type configured, ignoring data');
                return;
            }

            // Debug: Log the raw data to identify the source
            // console.log('📊 Processing scale data for type:', configuredScaleType);
            // console.log('📊 Raw data type:', typeof data, 'Length:', data?.length, 'First few bytes:', Array.isArray(data) ? data.slice(0, 10) : 'Not array');
            
            // Try to detect if this looks like ASCII data (serial) when we expect USB
            if (configuredScaleType === 'usb' && Array.isArray(data)) {
                const possibleAscii = data.every(byte => (byte >= 0x20 && byte <= 0x7E) || byte === 0x0D || byte === 0x0A);
                if (possibleAscii) {
                    console.warn('⚠️ Received ASCII-like data when expecting USB binary data, ignoring');
                    return;
                }
            }

            // Try to detect if this looks like binary data when we expect serial
            if (configuredScaleType === 'serial' && Array.isArray(data)) {
                const possibleBinary = data.some(byte => byte < 0x20 && byte !== 0x0D && byte !== 0x0A);
                if (possibleBinary && data.length < 50) { // USB HID reports are typically short
                    console.warn('⚠️ Received binary-like data when expecting serial ASCII data, ignoring');
                    return;
                }
            }

            // Only process data from the configured scale type
            if (configuredScaleType === 'usb') {
                handleUSBScaleData(data);
            } else if (configuredScaleType === 'serial') {
                handleSerialScaleData(data);
            } else if (configuredScaleType === 'simulation') {
                return;
            } else {
                console.warn('⚠️ Unknown scale type:', configuredScaleType);
            }
        } catch (error) {
            console.error('❌ Error processing scale data:', error);
        }
    }, [configuredScaleType, isDualScaleMode, isTransitioning]);

    const handleUSBScaleData = useCallback((data) => {
        try {
            // Validate data format for USB scale
            if (!Array.isArray(data) && !data.buffer) {
                console.warn('⚠️ Invalid USB data format, skipping:', typeof data);
                return;
            }

            // Convert the array of bytes into an Uint8Array
            const uint8Array = new Uint8Array(data);
            
            // Validate data length (USB HID scale data should be at least 6 bytes)
            if (uint8Array.length < 6) {
                console.warn('⚠️ USB data too short, skipping:', uint8Array.length, 'bytes');
                return;
            }
    
            // Get the underlying ArrayBuffer
            const buffer = uint8Array.buffer;
    
            // Create a DataView over the buffer
            const view = new DataView(buffer);
    
            const status = view.getUint8(1);
            const unit = view.getUint8(2);
            let scaling = view.getInt8(3);
            if (scaling === -1 || scaling === 255) scaling = -1; // treat 255 as -1
            const rawWeight = view.getUint16(4, true);
    
            let weight = rawWeight * Math.pow(10, scaling);
    
            // Validate weight is reasonable (reject astronomical values)
            if (weight > 1000000 || weight < -1000000) {
                console.warn('⚠️ Unreasonable weight detected, skipping:', weight);
                return;
            }
    
            if (status === 5) { // negative weight
                weight = -weight;
            } else if (status === 2) { // zero weight
                weight = 0;
            }
    
            // Show unit only if weight != 0
            let unitStr = "";
            if (weight !== 0) {
                unitStr = unit === 2 ? "g" : unit === 11 ? "oz" : "";
            }
    
            let weightStr = `${weight.toFixed((unit == 11 && weight != 0) ? 1 : 0)}${unitStr ? " " + unitStr : ""}`;
            
            // console.log('📊 USB Scale data processed:', weightStr);
            
            // Update state
            setCurrentWeight(weight > 0 ? weightStr : null);
            
            // In dual scale mode, also update appropriate scale weight
            if (isDualScaleMode) {
                if (config?.emptyScaleType === 'usb') {
                    setEmptyScaleWeight(weight > 0 ? weightStr : null);
                    setEmptyScaleStatus([2, 4, 5].includes(status) ? 'stable' : 'unstable');
                } else if (config?.fullScaleType === 'usb') {
                    setFullScaleWeight(weight > 0 ? weightStr : null);
                    setFullScaleStatus([2, 4, 5].includes(status) ? 'stable' : 'unstable');
                }
            }
            
            // Update weight status
            if ([2, 4, 5].includes(status)) {
                setWeightStatus('stable');
            } else {
                setWeightStatus('unstable');
            }
        } catch (error) {
            console.error('❌ Error processing USB scale data:', error);
        }
    }, [isDualScaleMode, config?.emptyScaleType, config?.fullScaleType]);

    const handleSerialScaleData = useCallback((data) => {
        try {
            // Convert buffer to string
            const dataString = Buffer.from(data).toString('ascii').trim();
            // console.log('📊 Serial scale raw data:', dataString);
            
            // Initialize defaults
            let weight = 0;
            let unit = 'lb';
            let status = 'stable';
            
            // Parse different possible formats
            // Format 1: "ST,GS,+123456 lb"
            if (dataString.includes(',')) {
                const parts = dataString.split(',');
                if (parts.length >= 3) {
                    status = parts[0] === 'ST' ? 'stable' : 'unstable';
                    const weightPart = parts[2];
                    const match = weightPart.match(/([\+\-]?\d+\.?\d*)\s*(\w+)/);
                    if (match) {
                        weight = parseFloat(match[1]);
                        unit = match[2];
                    }
                }
            }
            // Format 2: "   123456 lb ST" or "123456.0 lb"
            else {
                const match = dataString.match(/([\+\-]?\d+\.?\d*)\s*(\w+)\s*(\w+)?/);
                if (match) {
                    weight = parseFloat(match[1]);
                    unit = match[2];
                    if (match[3]) {
                        status = match[3] === 'ST' ? 'stable' : 'unstable';
                    }
                }
            }
            
            // Format 3: "ID_01:  123456.0 lb"
            if (dataString.includes('ID_') && dataString.includes(':')) {
                const colonIndex = dataString.indexOf(':');
                const weightPart = dataString.substring(colonIndex + 1).trim();
                const match = weightPart.match(/([\+\-]?\d+\.?\d*)\s*(\w+)/);
                if (match) {
                    weight = parseFloat(match[1]);
                    unit = match[2];
                    status = 'stable'; // Assume stable if not specified
                }
            }
            
            // Convert to tons based on the unit
            let weightInTons = 0;
            if (weight > 0) {
                const unitLower = unit.toLowerCase();
                if (unitLower === 'kg' || unitLower === 'kilogram' || unitLower === 'kilograms') {
                    weightInTons = weight / 1000; // kilograms to tons (metric)
                } else if (unitLower === 'g' || unitLower === 'gram' || unitLower === 'grams') {
                    weightInTons = weight / 1000000; // grams to tons (metric)
                } else if (unitLower === 'oz' || unitLower === 'ounce' || unitLower === 'ounces') {
                    weightInTons = weight / 32000; // ounces to tons (US short ton)
                } else {
                    // Assume pounds (lb, lbs, pound, pounds) or any other unit
                    weightInTons = weight / 2000; // pounds to tons (US short ton)
                }
            }
            
            // Format to 2 decimal places
            const weightStr = weightInTons > 0 ? `${weightInTons.toFixed(2)} Tons` : null;
            
            // Update state
            setCurrentWeight(weightStr);
            setWeightStatus(status);
            
            // In dual scale mode, also update appropriate scale weight
            if (isDualScaleMode) {
                if (config?.emptyScaleType === 'serial') {
                    setEmptyScaleWeight(weightStr);
                    setEmptyScaleStatus(status);
                } else if (config?.fullScaleType === 'serial') {
                    setFullScaleWeight(weightStr);
                    setFullScaleStatus(status);
                }
            }
            
        } catch (error) {
            console.error('❌ Error processing serial scale data:', error);
        }
    }, [isDualScaleMode, config?.emptyScaleType, config?.fullScaleType]);

    const handleAutoConnect = useCallback(async (config, onConnect) => {
        // console.log('🔗 handleAutoConnect called with config:', config);
        
        const isUSB = config.scaleType === 'usb';
        const isSerial = config.scaleType === 'serial';
        const isLoadRite = config.scaleType === 'loadrite';
        
        if ((!isUSB && !isSerial && !isLoadRite) || isConnectedRef.current) {
            // console.log('⏭️ Skipping auto-connect:', { 
            //     scaleType: config.scaleType,
            //     isConnected: isConnectedRef.current 
            // });
            return;
        }

        if (isUSB && !config.scaleDevice) return;
        if (isSerial && !config.serialPort) return;
        if (isLoadRite && (!config.loadriteAccessToken || !config.loadriteSiteId)) return;

        // console.log('🔗 Starting auto-connect to scale:', config.scaleType, isUSB ? config.scaleDevice : config.serialPort);
        
        setConnecting(true);
        try {
            if (isUSB) {
                if (onConnect) {
                    await onConnect('scale', { device: config.scaleDevice, type: 'usb' });
                }
                let ids = config.scaleDevice.split('_');
                // console.log('📡 Opening USB scale with IDs:', ids);
                
                // Convert string IDs to numbers for HID
                const vendorId = parseInt(ids[0], 10);
                const productId = parseInt(ids[1], 10);
                
                // console.log('📡 Parsed IDs - vendorId:', vendorId, 'productId:', productId);
                
                // Test the device first
                // console.log('🧪 Testing USB device before opening...');
                const testResult = await window.scaleAPI.testDevice(vendorId, productId);
                // console.log('🧪 Device test result:', testResult);
                
                if (!testResult.success) {
                    throw new Error(`Device test failed: ${testResult.error}`);
                }
                
                const result = await window.scaleAPI.openScale(vendorId, productId);
                // console.log('📡 USB Scale open result:', result);
            } else if (isSerial) {
                if (onConnect) {
                    await onConnect('scale', { 
                        device: config.serialPort, 
                        type: 'serial',
                        serialConfig: {
                            port: config.serialPort,
                            baudRate: config.baudRate || 9600,
                            dataBits: config.dataBits || 8,
                            parity: config.parity || 'none',
                            stopBits: config.stopBits || 1,
                            flowControl: config.flowControl || 'none'
                        }
                    });
                }
                
                // console.log('📡 Opening serial scale...');
                // This would be implemented in your scaleAPI
                const result = await window.scaleAPI.openSerialScale({
                    port: config.serialPort,
                    baudRate: config.baudRate || 9600,
                    dataBits: config.dataBits || 8,
                    parity: config.parity || 'none',
                    stopBits: config.stopBits || 1,
                    flowControl: config.flowControl || 'none'
                });
                // console.log('📡 Serial Scale open result:', result);
            } else if (isLoadRite) {
                if (onConnect) {
                    await onConnect('scale', { 
                        device: `LoadRite-${config.loadriteSiteId}`,
                        type: 'loadrite',
                        loadriteConfig: {
                            accessToken: config.loadriteAccessToken,
                            refreshToken: config.loadriteRefreshToken,
                            siteId: config.loadriteSiteId,
                            scaleId: config.loadriteScaleId,
                            pollingInterval: config.loadritePollingInterval || 5,
                            dataType: config.loadriteDataType || 'NewWeight'
                        }
                    });
                }
                
                // LoadRite connection is handled by the useLoadRiteScale hook
                // Just mark as connected if we have valid config
                isConnectedRef.current = true;
            }
            // console.log('✅ Scale connected automatically');
        } catch (error) {
            console.error('❌ Failed to connect scale:', error);
            isConnectedRef.current = false;
        } finally {
            setConnecting(false);
        }
    }, []);

    // Setup scale data listener
    const setupScaleDataListener = useCallback(() => {
        if (isDualScaleMode) {
            // In dual scale mode, we still need to set up listeners for USB/Serial scales
            // Check if we have USB/Serial scales configured
            const hasEmptyUSB = config?.emptyScaleType === 'usb';
            const hasEmptySerial = config?.emptyScaleType === 'serial';
            const hasFullUSB = config?.fullScaleType === 'usb';
            const hasFullSerial = config?.fullScaleType === 'serial';
            
            if (hasEmptyUSB || hasEmptySerial || hasFullUSB || hasFullSerial) {
                // console.log('🔧 Dual scale mode: Setting up USB/Serial listener');
                if (window.scaleAPI && window.scaleAPI.onData) {
                    window.scaleAPI.onData(handleScaleData);
                } else {
                    console.warn('⚠️ Scale API not available');
                }
            } else {
                // console.log('🔧 Dual scale mode: No USB/Serial scales configured, skipping listener setup');
            }
            return;
        }
        
        if (window.scaleAPI && window.scaleAPI.onData) {
            // console.log('🔧 Setting up scale data listener for type:', configuredScaleType);
            window.scaleAPI.onData(handleScaleData);
        } else {
            console.warn('⚠️ Scale API not available');
        }
    }, [handleScaleData, configuredScaleType, isDualScaleMode, config?.emptyScaleType, config?.fullScaleType]);

    // Cleanup scale data listener
    const cleanupScaleDataListener = useCallback(() => {
        if (window.electronAPI && window.electronAPI.removeAllListeners) {
            // console.log('🧹 Cleaning up scale data listener');
            window.electronAPI.removeAllListeners('scale-data');
        }
    }, []);

    // Handle scale connection events
    useEffect(() => {
        const handleScaleConnection = (isConnected) => {
            // console.log('Scale connection changed:', isConnected);
            isConnectedRef.current = isConnected;
            
            if (isConnected) {
                // Scale connected - setup listener
                setupScaleDataListener();
            } else {
                // Scale disconnected - cleanup and reset state
                cleanupScaleDataListener();
                setCurrentWeight(null);
                setWeightStatus('stable');
            }
        };

        // Listen for scale connection events
        if (window.electronAPI?.on) {
            window.electronAPI.on('scale-connected', () => handleScaleConnection(true));
            window.electronAPI.on('scale-disconnected', () => handleScaleConnection(false));
        }

        return () => {
            if (window.electronAPI?.removeAllListeners) {
                window.electronAPI.removeAllListeners('scale-connected');
                window.electronAPI.removeAllListeners('scale-disconnected');
            }
        };
    }, [setupScaleDataListener || (() => {}), cleanupScaleDataListener || (() => {})]);

    return {
        // Single scale data
        currentWeight,
        weightStatus,
        connecting,
        isConnectedRef,
        handleScaleData,
        handleUSBScaleData,
        handleSerialScaleData,
        handleAutoConnect,
        setupScaleDataListener,
        cleanupScaleDataListener,
        setCurrentWeight,
        setWeightStatus,
        
        // Dual scale data
        isDualScaleMode,
        emptyScaleWeight,
        emptyScaleStatus,
        fullScaleWeight,
        fullScaleStatus,
        setEmptyScaleWeight,
        setEmptyScaleStatus,
        setFullScaleWeight,
        setFullScaleStatus
    };
};